import { useUrl } from '@shopify/hydrogen';
import { useCallback, useMemo } from 'react';
import { TypedParamsNavigationStructure } from './useNavigation';
import { merge } from 'lodash';
import { UTM_PARAMS } from '../../utils/urlUtils';
import { ActionAtLoadValues, ADDON_TYPES } from '@lib/home-wizard/domain-types';
import { possibleFilterKeys } from '@lib/home-wizard/models/generic-models';

const possibleParamKeys = [
    'featureToggles', // an array of features which can be toggled on or off, separated with a ";"
    'registered',
    'delivery_setting', // used for A/B test on delivery time text
    'project_ids', // list of all project ids
    'share_ids', // list of all shared project ids
    'project_id', // current project that is being changed
    'configuration_id', // current configuration that is being viewed
    'user', // homeowner or installer
    'cache_buster', // force the page to reload with a new cache because the url has changed
    'base_product', // base product of the current door
    'conf_route', // how the user entered the configurator (home or pdp)
    'page', // page in the PLP
    'search', // search term for PLP
    'sort', // sorting option for PLP
    'localDiscountPercentage',
    'draft', // is draft order
    'share', // save to localstorage
    'from', // the path to be put in the breadcrumbs
    'error',
    'order_id', // the id of the project that was submitted to the BMG
    'reset_configuration',
    'referrer',
    'model',
    'successPasswordReset', //used to show a message that password reset was successfully
    'successChangePassword', //used to show a message that password change was successfully
    'confirmationOption', // all possible confirmation pages
    'taxMode',
    'projectCategory', // category to filter on for the project overview page
    'projectPage', // page to filter on for the project overview page
    'activeProjectId', //Current active project
    'shareProjectPage', // page to filter on for the project overview page's shared projects
    'customerGroup', // de dealer customer group
    'ssoSignin', // de dealer customer group
    ...ActionAtLoadValues,
    ...ADDON_TYPES,
    ...possibleFilterKeys, // all possible filter options
    ...UTM_PARAMS, // params used for the utm campaigns, needed so they don't get cleared
] as const;

export type PossibleParamKey = typeof possibleParamKeys[number];
export type TypedParamsStructure = Partial<Record<PossibleParamKey, string>>;

export interface UrlParam {
    name: PossibleParamKey;
    value: string;
}

export interface BuildUrlOptions {
    path?: string;
    anchor?: string;
    params?: TypedParamsNavigationStructure;
    method?: 'MERGE' | 'SET';
    paramsToRemove?: PossibleParamKey[];
}

export const useUrlParams = (options?: { searchParams?: URLSearchParams }) => {
    const url = useUrl();
    const { searchParams, pathname } = url;

    const _searchParams = options?.searchParams ?? searchParams;

    const isParam = useCallback(
        ({ name, value, delimiter }: { name: string; value: string; delimiter?: string }) =>
            !!_searchParams
                .get(name)
                ?.split(delimiter ?? ',')
                .filter(Boolean)
                .includes(value),
        [_searchParams],
    );

    const setParam = useCallback(
        ({ name, value }: UrlParam): void => {
            _searchParams.set(name, value);
        },
        [_searchParams],
    );

    const unsetParam = useCallback(
        (name: UrlParam['name']): void => {
            _searchParams.has(name) && _searchParams.delete(name);
        },
        [_searchParams],
    );

    const params: TypedParamsStructure = useMemo(() => {
        return possibleParamKeys.reduce<TypedParamsStructure>((total, key) => {
            const value = _searchParams.get(key);
            return {
                ...total,
                [key]: value ?? undefined,
            };
        }, {});
    }, [_searchParams]);

    const buildUrl = useCallback(
        (options: BuildUrlOptions) => {
            const { path, params: _params = params, method = 'MERGE', paramsToRemove, anchor } = options;
            // if we provide parameters to clean, add them to a record and merge this with the params
            const defaultParamsToRemove: PossibleParamKey[] = ['error'];
            const paramsToClean = [...defaultParamsToRemove, ...(paramsToRemove ?? [])].reduce<
                Record<PossibleParamKey, string>
            >((total, param) => {
                return options.params?.[param]
                    ? { ...total }
                    : {
                          ...total,
                          [param]: '',
                      };
            }, {} as Record<PossibleParamKey, string>);
            const newParamsBase: TypedParamsNavigationStructure =
                method === 'MERGE' ? merge({}, params, _params, paramsToClean) : _params;

            // remove parameters that are specifically set to be empty, undefined or null
            const newParamsPurged = Object.keys(newParamsBase).reduce<TypedParamsStructure>((params, key) => {
                const value = newParamsBase[key];
                if (value === null || value === undefined || value === '') return params;
                return {
                    ...params,
                    [key]: value,
                };
            }, {});

            const anchorSuffix = anchor ? '#' + anchor : '';
            const newPath = (path ?? pathname).split('?')[0];

            return {
                url: newPath + '?' + new URLSearchParams(newParamsPurged).toString() + anchorSuffix,
            };
        },
        [params, pathname],
    );

    return useMemo(() => {
        return { isParam, searchParams: _searchParams, setParam, unsetParam, params, pathname, buildUrl };
    }, [_searchParams, buildUrl, isParam, params, pathname, setParam, unsetParam]);
};
